import { useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { showAlertWithConfirmCancel } from "../../server/ShowAlert";
import { CategoryUpdate, getBySlug } from "../../server/Category";
import { setCategoryItemsSource } from "../../server/Product";

const buildPositionPayload = (items, destinationIndex) => {
  if (destinationIndex === 0) return { kind: "prepend" };
  if (destinationIndex === items.length - 1) return { kind: "append" };

  return {
    kind: "betweenByRank",
    before: items[destinationIndex - 1]?.rank,
    after: items[destinationIndex + 1]?.rank,
  };
};

const replaceChildrenBySlug = (node, targetSlug, nextChildren) => {
  if (!node || !targetSlug) return node;

  if (Array.isArray(node)) {
    return node.map((item) =>
      replaceChildrenBySlug(item, targetSlug, nextChildren),
    );
  }

  if (node?.slug === targetSlug) {
    return {
      ...node,
      children: nextChildren,
    };
  }

  if (Array.isArray(node.children)) {
    return {
      ...node,
      children: node.children.map((child) =>
        replaceChildrenBySlug(child, targetSlug, nextChildren),
      ),
    };
  }

  return node;
};

export default function useCategoryReorder({
  items,
  setItems,
  enabled,
  stationId,
  isClientView,
  includeInactive,
  refreshCategorySlug = null,
  optimisticParentSlug = null,
}) {
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const categoryItemsSource = useSelector(
    (state) => state.displayProduct.categoryItemsSource,
  );

  const dragStateRef = useRef({ sourceSlug: null });
  const suppressClickRef = useRef(false);

  const resolveRefreshSlug = useCallback(() => {
    if (refreshCategorySlug) return refreshCategorySlug;
    if (!pathname.startsWith("/category/")) return null;
    return pathname.split("/")[2] || null;
  }, [pathname, refreshCategorySlug]);

  const reorderBySlug = useCallback(
    (sourceSlug, destinationSlug) => {
      if (!enabled) return;
      if (!sourceSlug || !destinationSlug) return;
      if (sourceSlug === destinationSlug) return;

      const sourceIndex = items.findIndex((cat) => cat?.slug === sourceSlug);
      const destinationIndex = items.findIndex(
        (cat) => cat?.slug === destinationSlug,
      );
      if (sourceIndex < 0 || destinationIndex < 0) return;

      const originalItems = [...items];
      const reorderedItems = [...items];
      const [movedItem] = reorderedItems.splice(sourceIndex, 1);
      reorderedItems.splice(destinationIndex, 0, movedItem);
      const position = buildPositionPayload(reorderedItems, destinationIndex);

      showAlertWithConfirmCancel({
        msgTitle: "האם לעדכן את סדר הקטגוריות?",
        confirmText: "אישור",
        cancelText: "ביטול",
        onConfirm: async () => {
          setItems(reorderedItems);

          const previousSource = categoryItemsSource;
          const optimisticSource =
            optimisticParentSlug && previousSource
              ? replaceChildrenBySlug(
                  previousSource,
                  optimisticParentSlug,
                  reorderedItems,
                )
              : null;

          if (optimisticSource) {
            dispatch(setCategoryItemsSource(optimisticSource));
          }

          try {
            const res = await dispatch(
              CategoryUpdate(
                {
                  slug: movedItem?.slug,
                  Title: "position",
                  value: position,
                  Mkt: movedItem?.id,
                  rank: movedItem?.rank,
                },
                stationId,
              ),
            );

            if (res?.isAxiosError || res instanceof Error) {
              throw res;
            }

            const slugToRefresh = resolveRefreshSlug();
            if (slugToRefresh) {
              const refreshedCategory = await getBySlug(
                stationId,
                slugToRefresh,
                isClientView,
                includeInactive,
              );

              if (refreshedCategory) {
                dispatch(setCategoryItemsSource(refreshedCategory));
              }
            }
          } catch (error) {
            console.error("Failed to update category position:", error);
            setItems(originalItems);
            if (previousSource) {
              dispatch(setCategoryItemsSource(previousSource));
            }
          }
        },
        onCancel: () => {
          setItems(originalItems);
        },
      });
    },
    [
      categoryItemsSource,
      dispatch,
      enabled,
      includeInactive,
      isClientView,
      items,
      optimisticParentSlug,
      resolveRefreshSlug,
      setItems,
      stationId,
    ],
  );

  const handleDragStart = useCallback(
    (slug) => {
      if (!enabled) return;
      dragStateRef.current = { sourceSlug: slug };
      suppressClickRef.current = true;
    },
    [enabled],
  );

  const handleDragOver = useCallback(
    (event) => {
      if (!enabled) return;
      event.preventDefault();
    },
    [enabled],
  );

  const handleDrop = useCallback(
    (event, destinationSlug) => {
      if (!enabled) return;
      event.preventDefault();
      reorderBySlug(dragStateRef.current.sourceSlug, destinationSlug);
      dragStateRef.current = { sourceSlug: null };
      setTimeout(() => {
        suppressClickRef.current = false;
      }, 0);
    },
    [enabled, reorderBySlug],
  );

  const handleDragEnd = useCallback(() => {
    dragStateRef.current = { sourceSlug: null };
    setTimeout(() => {
      suppressClickRef.current = false;
    }, 0);
  }, []);

  return {
    canReorder: Boolean(enabled),
    handleDragStart,
    handleDragOver,
    handleDrop,
    handleDragEnd,
    suppressClickRef,
  };
}
